<template>
  <div class="card">
    <div class="card-header">
      <div class="card-title">
        <i class="fa-solid fa-users"></i>
        <h2>Team Players</h2>
      </div>
      <button v-if="userRole === 'owner'" class="btn-text" @click="$emit('invite')">
        <i class="fa-solid fa-user-plus"></i>
        Invite
      </button>
    </div>
    <div class="card-body">
      <div v-if="players.length > 0" class="players-list">
        <transition-group name="booking-list">
          <div 
            v-for="player in players.slice(0, limit)" 
            :key="player.member_id"
            class="player-item booking-card"
          >
            <div class="booking-header">
              <div class="booking-customer">
                <div class="customer-avatar">
                  <i class="fa-solid fa-user"></i>
                </div>
                <div class="customer-info">
                  <h4>{{ player.name }}</h4>
                  <div class="customer-contact">
                    <span>{{ player.position || 'Player' }}</span>
                  </div>
                </div>
              </div>
              <div class="booking-meta">
                <span class="booking-status" :class="player.status">
                  {{ player.status }}
                </span>
              </div>
            </div>
            <div class="booking-body">
              <div class="booking-details">
                <div class="time-details">
                  <div class="time-slot">
                    <i class="fa-solid fa-user-tag"></i>
                    {{ player.role }}
                  </div>
                  <div v-if="player.is_captain" class="duration">
                    <i class="fa-solid fa-crown"></i>
                    Captain
                  </div>
                </div>
                <div class="booking-amount">
                  <div class="amount-label">Joined</div>
                  <div class="amount-value">{{ formatDate(player.joined_at) }}</div>
                </div>
              </div>
              <div v-if="player.stats" class="booking-notes">
                <i class="fa-solid fa-chart-bar"></i>
                <p>
                  {{ player.stats.matches || 0 }} matches • 
                  {{ player.stats.goals || 0 }} goals • 
                  {{ player.rating || 'N/A' }} rating
                </p>
              </div>
            </div>
            <div v-if="canRemovePlayer(player)" class="booking-actions">
              <button class="btn-action btn-danger" @click.stop="$emit('remove', player.member_id)">
                <i class="fa-solid fa-user-minus"></i>
                Remove
              </button>
            </div>
          </div>
        </transition-group>
      </div>
      <div v-else class="empty-state">
        <div class="empty-icon">
          <i class="fa-solid fa-users-slash"></i>
        </div>
        <h3>No Team Members</h3>
        <p>Invite players to build your team</p>
        <button v-if="userRole === 'owner'" class="btn-refresh" @click="$emit('invite')">
          <i class="fa-solid fa-user-plus"></i>
          Invite Players
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TeamPlayers',
  props: {
    players: {
      type: Array,
      default: () => []
    },
    userRole: {
      type: String,
      default: 'owner'
    },
    limit: {
      type: Number,
      default: 5
    },
    formatDate: {
      type: Function,
      required: true
    }
  },
  emits: ['invite', 'remove'],
  methods: {
    canRemovePlayer(player) {
      if (this.userRole !== 'owner') return false;
      return String(player?.role || '').toLowerCase() !== 'owner';
    }
  }
};
</script>

<style scoped>
.card {
  background: var(--card-bg);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.card-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.btn-text {
  background: none;
  border: none;
  color: var(--section-icon);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-text:hover {
  color: var(--heading-color);
  transform: translateX(5px);
}

.card-body {
  padding: 30px;
}

.players-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.booking-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.booking-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--card-hover-border);
}

.booking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.booking-customer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.customer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--subtext-color);
}

.customer-info h4 {
  margin: 0 0 4px 0;
  font-size: 0.95rem;
  color: var(--heading-color);
}

.customer-contact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.8rem;
  color: var(--subtext-color);
}

.booking-meta {
  display: flex;
  align-items: center;
  gap: 15px;
}

.booking-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.booking-status.active { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}
.booking-status.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.booking-body {
  margin-bottom: 15px;
}

.booking-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.time-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-slot, .duration {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--heading-color);
}

.booking-amount {
  text-align: right;
}

.amount-label {
  font-size: 0.75rem;
  color: var(--subtext-color);
  margin-bottom: 2px;
}

.amount-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--heading-color);
}

.booking-notes {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 15px;
}

.booking-notes i {
  color: var(--subtext-color);
  margin-top: 2px;
}

.booking-notes p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--subtext-color);
  line-height: 1.4;
}

.booking-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-action {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  min-width: 100px;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid var(--border-color-hover);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

.btn-primary {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.btn-danger {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--subtext-color);
}

.empty-state h3 {
  color: var(--heading-color);
  margin: 0 0 10px 0;
}

.empty-state p {
  color: var(--subtext-color);
  margin-bottom: 20px;
}

.btn-refresh {
  background: var(--edit-gradient);
  color: var(--button-color);
  border: none;
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-refresh:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px var(--edit-shadow);
}

.booking-list-enter-active,
.booking-list-leave-active {
  transition: all 0.4s ease;
}

.booking-list-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.booking-list-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

.booking-list-move {
  transition: transform 0.4s ease;
}
</style>
